import { AuthToken } from "../types/auth";
import decodeJwt from "jwt-decode";

/**
 * The signed-in user's Hasura role, read from the ACCESS TOKEN.
 *
 * WHY NOT sessionStorage: login stashes the role in
 * `sessionStorage.hasuraDefaultRole` while the token itself lives in
 * `localStorage.ron-auth`. Those two have different lifetimes — sessionStorage
 * is per-tab and does not survive a new tab, while localStorage is shared
 * across every tab and persists. So it is entirely possible to be holding a
 * perfectly valid admin token with NO role in sessionStorage.
 *
 * When that happens nothing errors. The app stays logged in and silently
 * treats an admin as a non-admin: on Stripe Connect the loader swaps to the
 * restricted `where` and returns a handful of rows instead of the full list,
 * which reads as missing data rather than as a permissions problem. Logging
 * out and back in "fixes" it only because login re-stamps sessionStorage in
 * that tab.
 *
 * The token is the authority — it is what Hasura actually enforces against —
 * and it has exactly the right lifetime. sessionStorage stays as a fallback
 * for the moment before a token is present.
 */
export const getDashboardRole = (): string | null => {
  const accessToken = localStorage.getItem("ron-auth");
  if (accessToken) {
    try {
      const decodedToken: AuthToken = decodeJwt(accessToken);
      const role =
        decodedToken["https://hasura.io/jwt/claims"]?.["x-hasura-default-role"];
      if (role) return role;
    } catch {
      // Malformed/expired token — fall through to the stash below.
    }
  }
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage.getItem("hasuraDefaultRole");
};

export const isAdminUser = (): boolean => getDashboardRole() === "admin";

export const getUserId = () => {
  const accessToken = localStorage.getItem("ron-auth");
  if (!accessToken) return null;
  const decodedToken: AuthToken = decodeJwt(accessToken);
  const hasuraClaims = decodedToken["https://hasura.io/jwt/claims"];

  const user = hasuraClaims["x-hasura-user-id"];
  return user;
};
